/**
 * The lab's guide to the cilium statuses, and its real examples.
 *
 * GENERATED by scripts/make-quest-examples.py from the lab's own data, so
 * that no number or sentence here was typed by hand: the reference sheet
 * (Status, When to use, Example; 1RPoLl36rx6nl17NTqVsvcC5FBo-wgDmkvszv0sfYigU) and the
 * lab's saved view of each example (each one's `source`). Do not edit it; run
 * the script again. Nothing here is made up: a status the sheet gives no
 * example for has none.
 *
 * Two uses. The Submit form shows the lab's "when to use" line for the status
 * chosen. And "Here's a reference" in the Instructions opens one view, in a
 * new tab, holding the example cells with the lab's own annotations in the
 * quest's three layers; each basal body carries its status and when to use
 * it as its description, so that layer's list is the index: the arrows over
 * the list step from one example to the next, and a row's jump button goes
 * to that one. The cells are named by the ids the lab's views carry; an edit
 * to one later leaves the example showing the cell as the lab annotated it
 * (Ames 2026-10-13: "a ng link that has layers/annotations and descriptions
 * to demo the various things on real cell with real example annotations").
 */
import { findDatasetByCanonical } from './datasets';

/** When to use each status, in the lab's words (the sheet's column B). */
export const QUEST_STATUS_GUIDE: Record<string, string> = {
  "Cilium annotated": "Cilium emerges from the cell, both centrioles and cilium centerline are annotated",
  "Cilium annotated (fixation artifact)": "Cilium structure is visible near the surface of the cell, but appears attached/internal to the soma. This is caused by membranes fusing during data collection.",
  "Need help": "You can't find any cilium or centrioles, or some other issue that stops you from annotating the cell. Good to leave clarifying notes in most cases!",
  "Need help: One centriole missing": "You found one centriole, but the other is MIA",
  "No cilium": "Nothing found. This should only be used after review (use Need Help if you are the first annotator)",
  "No cilium (BB & DC clear)": "No cilium, but centrioles are visible and clearly differentiated (basal body closer to the surface).",
  "No cilium (BB & DC unclear; internal centriole pair)": "No cilium, centriole positions make it unclear which is the basal body and which is the daughter",
  "Skipped (low quality)": "Extensive data artifacting, missing slides, no soma etc, make it too difficult to ID cilium or centrioles",
};

/** The layers as the lab's own views name them, in the lab's order. */
export const QUEST_EXAMPLE_LAYERS = {
  centerline: "Cilium centerline",
  daughterCentriole: "Daugther centriole",
  basalBody: "Basal body",
};

export interface QuestExample {
  status: string;
  /** The lab's saved view this came from. */
  source: string;
  /** The cell, as the lab's view names it. */
  root: string;
  position: number[];
  crossSectionScale: number;
  projectionScale: number;
  projectionOrientation?: number[];
  basalBody: number[];
  daughterCentriole: number[];
  /** The cilium centerline, a polyline; absent where the lab drew none. */
  centerline?: number[][];
}

export const QUEST_EXAMPLES: QuestExample[] = [
  {
    status: "Cilium annotated",
    source: "https://global.daf-apis.com/nglstate/api/v1/6292320998653952",
    root: "720575940561630366",
    position: [17536.08203125, 26179.416015625, 737.5],
    crossSectionScale: 0.36143305429464245,
    projectionScale: 907.1795328941247,
    projectionOrientation: [0.5656347870826721, 0.14847055077552795, -0.2089623659849167, -0.7838038802146912],
    basalBody: [17393.1953125, 26087.73828125, 748.4999389648438],
    daughterCentriole: [17340.396484375, 26123.716796875, 742.4999389648438],
    centerline: [
      [17404.837890625, 26084.09375, 751.5000610351562],
      [17433.779296875, 26075.064453125, 761.5],
      [17461.634765625, 26071.8125, 767.5],
      [17486.908203125, 26073.48828125, 771.4999389648438],
      [17507.166015625, 26081.435546875, 771.4999389648438],
      [17524.53125, 26098.7734375, 769.5000610351562],
      [17536.197265625, 26120.626953125, 764.5],
      [17545.603515625, 26155.666015625, 754.4999389648438],
      [17548.291015625, 26215.197265625, 737.5],
    ],
  },
  {
    status: "No cilium (BB & DC clear)",
    source: "https://global.daf-apis.com/nglstate/api/v1/6390751482085376",
    root: "720575940557564614",
    position: [16685.5, 26086.876953125, 614.5],
    crossSectionScale: 1.2,
    projectionScale: 497.8706836786392,
    projectionOrientation: [0.8107180595397949, -0.28611478209495544, 0.0877659022808075, -0.5031617283821106],
    basalBody: [16678.435546875, 26096.8515625, 610.4999389648438],
    daughterCentriole: [16674.8359375, 26131.630859375, 606.5],
  },
  {
    status: "No cilium (BB & DC unclear; internal centriole pair)",
    source: "https://global.daf-apis.com/nglstate/api/v1/5522129563418624",
    root: "720575940571019529",
    position: [6666.69677734375, 60346.91796875, 1203.5],
    crossSectionScale: 1.2,
    projectionScale: 1652.9888822158648,
    projectionOrientation: [-0.1984185427427292, 0.93731290102005, -0.17995265126228333, -0.22291624546051025],
    basalBody: [6665.4970703125, 60400.09375, 1203.4998779296875],
    daughterCentriole: [6681.09716796875, 60326.87109375, 1212.4998779296875],
  },
];

/** The colour key: centerline green, daughter centriole red, basal body gold. */
const COLORS = { centerline: '#7CFFB2', daughterCentriole: '#e06060', basalBody: '#f5d142' };

/** The view of the examples, as a neuroglancer state. */
export function questExamplesState(): any {
  const dataset = findDatasetByCanonical('stroeh_mouse_retina');
  const first = QUEST_EXAMPLES[0];
  const base = (dataset?.layers ?? []).map((l: any) =>
      typeof l?.type === 'string' && l.type.startsWith('segmentation') ?
          { ...l, segments: QUEST_EXAMPLES.map(e => e.root) } :
          { ...l });
  const says = (e: QuestExample) => `Example: ${e.status}. ${QUEST_STATUS_GUIDE[e.status] || ''}`.trim();
  const annotationLayer = (name: string, color: string, tool: string, annotations: any[]) =>
      ({ type: 'annotation', source: 'local://annotations', tab: 'annotations', name, annotationColor: color, tool, annotations });
  return {
    dimensions: { x: [1.6e-8, 'm'], y: [1.6e-8, 'm'], z: [4e-8, 'm'] },
    position: first.position,
    crossSectionScale: first.crossSectionScale,
    projectionScale: first.projectionScale,
    ...(first.projectionOrientation ? { projectionOrientation: first.projectionOrientation } : {}),
    layout: 'xy-3d',
    layers: [
      ...base,
      annotationLayer(QUEST_EXAMPLE_LAYERS.centerline, COLORS.centerline, 'annotatePolyline',
          QUEST_EXAMPLES.filter(e => e.centerline).map((e, i) => ({
            type: 'polyline', id: `example-centerline-${i + 1}`, points: e.centerline,
            description: `Example: ${e.status}. The cilium centerline.`,
          }))),
      annotationLayer(QUEST_EXAMPLE_LAYERS.daughterCentriole, COLORS.daughterCentriole, 'annotatePoint',
          QUEST_EXAMPLES.map((e, i) => ({
            type: 'point', id: `example-daughter-centriole-${i + 1}`, point: e.daughterCentriole,
            description: `Example: ${e.status}. The daughter centriole.`,
          }))),
      // The basal bodies carry each status and when to use it: this layer's
      // list is the index of the examples.
      annotationLayer(QUEST_EXAMPLE_LAYERS.basalBody, COLORS.basalBody, 'annotatePoint',
          QUEST_EXAMPLES.map((e, i) => ({
            type: 'point', id: `example-basal-body-${i + 1}`, point: e.basalBody, description: says(e),
          }))),
    ],
    selectedLayer: { layer: QUEST_EXAMPLE_LAYERS.basalBody, visible: true },
  };
}

/** The examples view as a link to this site. */
export function questExamplesUrl(): string {
  return `${window.location.origin}${window.location.pathname}#!${encodeURIComponent(JSON.stringify(questExamplesState()))}`;
}

/** Open the examples in a new tab, so the view in hand, and any annotations
 *  being placed in it, stay as they are. */
export function openQuestExamples(): void {
  window.open(questExamplesUrl(), '_blank', 'noopener');
}
